import airbridge from 'airbridge-web-sdk-loader'
import { EventOption, InitOption } from 'airbridge-web-sdk-loader/build/airbridge_api'

import { AirbridgeEvents } from '../types/events'
import { Environment } from './environment'

const Setting = {
  production: {
    app: 'gangnamunni',
    webToken: '39c2048e6dd64a32b6076ea1d3acc406',
  },
  development: {
    app: 'gangnamunnidev',
    webToken: '90835c1682b4429ba6488e1a1d505b9d',
  },
}

type Config = { environment: Environment }
export interface AirbridgeModelInterface {
  initialize: ({ config, userProperties, options }: AirbridgeInitialize) => void
  sendEvent: (category: AirbridgeEvents, options?: EventOption) => void
  setDeviceAlias: (key: AirbridgeDeviceAlias, deviceId: string) => void
  setUserId: (userId: string) => void
  setUserAttributes: (attributes: UserProperties) => void
  signOut: () => void
  clearUser: () => void
}

export interface UserProperties {
  userId?: string
  country: string
  age?: string | number
  myHospitalDistrictId?: string
  [appDefinedProperty: string]: any
  amplitudeDeviceId?: string
}
export interface AirbridgeInitialize {
  config: Config
  userProperties: UserProperties
  options?: Omit<InitOption, 'app' | 'webToken'>
}

export enum AirbridgeDeviceAlias {
  Amplitude = 'amplitude_device_id'
}

export class AirbridgeModel implements AirbridgeModelInterface {
  private static instance: AirbridgeModel
  private app?: string
  private webToken?: string
  private userProperties?: UserProperties

  constructor() {
    if (AirbridgeModel.instance) return AirbridgeModel.instance

    AirbridgeModel.instance = this
  }

  initialize = ({ config, userProperties, options }: AirbridgeInitialize): void => {
    try {
      if (!userProperties || !Object.keys(userProperties).length) return
      const { userId, ...restUserProperties } = userProperties || {}
      const { environment } = config
      const { app, webToken } = Setting[environment]
      this.app = app
      this.webToken = webToken
      this.userProperties = userProperties
  
      airbridge.init({
        app: this.app,
        webToken: this.webToken,
        ...(userId ? { user: {
          externalUserID: userId,
          attributes: restUserProperties,
        } } : {}),
        ...options,
      })
    } catch (error) {
      console.error('airbridge initialize error', error)
    }
  }

  public sendEvent = (category: AirbridgeEvents, options?: EventOption): void => {
    if (!this.userProperties) return

    const label = `${this.userProperties.country},${this.userProperties.myHospitalDistrictId}`
    airbridge.events.send(category, {
      action: this.userProperties?.age?.toString() || '',
      label,
      ...options,
    })
  }

  public setUserId = (userId: string): void => {
    airbridge.setUserID(userId)
  }

  public setUserAttributes = (attributes: UserProperties): void => {
    Object.entries(attributes).forEach(([key, value]) => {
      airbridge.setUserAttribute(key, value)
    })
  }

  /**
   * https://help.airbridge.io/ko/developers/web-sdk#%EB%A1%9C%EA%B7%B8%EC%95%84%EC%9B%83
   */
  public signOut = (): void => {
    airbridge.events.send(AirbridgeEvents.USER_SIGN_OUT)
    this.clearUser()
  }

  public clearUser = (): void => {
    airbridge.clearUser()
  }

  /**
   * https://help.airbridge.io/ko/developers/amplitude-v2#web
   * airbridge.init 에서 { autoStartTrackingEnabled: false } 로 설정해야만 사용 가능합니다.
   */
  public setDeviceAlias = (key: AirbridgeDeviceAlias, deviceId: string): void => {
    airbridge.setDeviceAlias(key, deviceId)
    airbridge.startTracking()
  }
}

export const airbridgeInstance: AirbridgeModel = new AirbridgeModel()
export default airbridge
